import React, { useState } from "react"
import { useTranslation } from "react-i18next"
import { useDispatch } from "store"
import { deletePatientAction, getPatientListByCollaboratorAction, getAllPatientsAction } from "store/global/actions"
import { useCurrentRole } from "store/hooks"
import { TextInputV2, TypographyV2, ButtonV2 } from "components/v2"
import Modal from "components/common/Modal"
import { classNames } from "utils"
import { CloseIcon } from "components/icons"
import { toast } from "react-toastify"

const DeletePatientModal = ({
    isOpen,
    onClose,
    onDeleted,
    params
}: {
    isOpen: boolean
    onClose: () => void
    onDeleted?: () => void
    params: any
}) => {
    const { t } = useTranslation("patient")
    const dispatch = useDispatch()
    const currentRole = useCurrentRole()
    const [deleteConfirmation, setDeleteConfirmation] = useState("")
    const [isDeleting, setIsDeleting] = useState(false)

    const onSubmit = async () => {
        try {
            setIsDeleting(true)

            // Delete the patient
            await dispatch(deletePatientAction(params.user_id))

            // Refresh the patient lists to get updated data
            await dispatch(getPatientListByCollaboratorAction()) // Refresh my patients list

            // If user is admin, also refresh all patients list
            if (currentRole?.role === "admin" && !onDeleted) {
                await dispatch(getAllPatientsAction())
            }

            toast.success(t("patient:portalNotifications.patient_deleted"))
            setIsDeleting(false)
            onDeleted?.()
            onClose()
        } catch (error) {
            setIsDeleting(false)

            console.error(error)
            toast.error(t("common:error"))
        }
    }

    return (
        <Modal show={isOpen} onClose={onClose} centered cardClassName="w-2/4  max-w-screen-lg">
            <div className="flex flex-col w-full gap-4">
                <div className="p-4 flex justify-between border-b border-border-blue">
                    <TypographyV2 width="bold" type="base" className="text-med-black">
                        {t("common:deletePatient")}
                    </TypographyV2>
                    <div className="flex items-center">
                        <CloseIcon onClick={onClose} className="cursor-pointer text-[#9CA3AF]" height={20} width={20} />
                    </div>
                </div>
                <div className="flex flex-col w-full p-4">
                    <TypographyV2 type="lg" className="mb-2">
                        {t("deletePatient.deletePatientConfirmation")}
                    </TypographyV2>
                    <TypographyV2 type="base" className="text-gray-500 mb-4">
                        {t("deletePatient.deletePatientConfirmationInputType")}{" "}
                        <span className="font-[900] italic text-red-500">{`DELETE ${params.email}`}</span>{" "}
                        {t("deletePatient.deletePatientConfirmationInputConfirm")}
                    </TypographyV2>
                    <TextInputV2
                        value={deleteConfirmation}
                        onChange={(e) => setDeleteConfirmation(e)}
                        placeholder={`DELETE ${params.email}`}
                    />
                </div>
                <div className={classNames("flex p-4 border-t border-border-blue justify-end")}>
                    <div className="flex gap-2.5 justify-end">
                        <ButtonV2
                            variant="light-gray"
                            onClick={onClose}
                            isDisabled={isDeleting}
                            text={t("common:cancel")}
                        />
                        <ButtonV2
                            variant="danger"
                            isDisabled={deleteConfirmation !== `DELETE ${params.email}` || isDeleting}
                            onClick={onSubmit}
                            text={isDeleting ? t("common:deleting") : t("common:delete")}
                        />
                    </div>
                </div>
            </div>
        </Modal>
    )
}

export default DeletePatientModal
